import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AiTool, AiAgent } from '@rubii/entity/src/mdb-entities/ai';
import { ClientToolRunner } from '@rubii/sdk-web-openai/src/classes';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { LlmVendor } from '@rubii/sdk-web-openai/src/types/vendor-llm.type';
import { MarkdownModalComponent, MarkdownModalData } from '../ai-agent/markdown-modal.component';
import { MarkdownService } from '../../../../services/markdown.service';
import { ClientService } from '../../../../services/client.service';
import { Location } from '@angular/common';
import { UtilService } from '../../../../services/util.service';

@Component({
  selector: 'app-ai-tool',
  templateUrl: './ai-tool.component.html',
  styleUrls: ['./ai-tool.component.scss']
})
export class AiToolComponent implements OnInit, OnDestroy {
  vendors: LlmVendor[] = [];
  models: Record<LlmVendor, string[]> = {} as Record<LlmVendor, string[]>;
  tools: AiTool[] = [];
  agents: AiAgent[] = [];
  filteredTools: AiTool[] = [];
  selectedTool: AiTool | null = null;
  
  loading = false;
  searchQuery: string = '';
  isEditMode = false;
  requiredParamsStr: string = '';
  
  // tool runner
  toolRunner: ClientToolRunner;

  extraObj: any = {};
  
  paramValues: Record<string, string> = {};
  toolResult: any = null;
  isRunning = false;
  
  // For editing parameters as JSON
  paramJsonValues: Array<{
    key: string;
    valueJson: string;
    error?: string;
  }> = [];

  factoryParamJsonValues: Array<{
    key: string;
    valueJson: string;
    error?: string;
  }> = [];

  constructor(
    public markdown: MarkdownService,
    private client: ClientService,
    private cdRef: ChangeDetectorRef,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private utilService: UtilService
  ) {
    // Add event listener for keydown
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  // Add cleanup on component destroy
  ngOnDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
    // Clean up URL parameter
    // this.utilService.removeUrlParamWithoutTriggeringNavigation('tool_id');
  }

  private handleKeyDown(event: KeyboardEvent) {
    // Check if Enter key is pressed
    if (event.key === 'Enter') {
      // In edit mode, Ctrl/Cmd + Enter triggers save
      if (this.isEditMode && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        this.saveToolChanges();
        return;
      }

      // In run mode, Enter triggers run
      if (!this.isEditMode) {
        // Allow running tool when:
        // 1. Not in a textarea
        // 2. Not in a contenteditable element
        // 3. In a text input field that's part of the parameters
        const isParameterInput = event.target instanceof HTMLInputElement && 
                               (event.target.closest('.parameter-input') || 
                                event.target.closest('.search-field'));
        
        if (isParameterInput || 
            (!(event.target instanceof HTMLTextAreaElement) && 
             !(event.target as HTMLElement)?.isContentEditable)) {
          this.runTool();
        }
      }
    }
  }

  async ngOnInit() {
    await this.getToolList();
    await this.getVendors();
    
    // Check for tool_id in query params
    this.route.queryParams.subscribe(async (params) => {
      if (params['tool_id']) {
        const tool = this.tools.find(t => t._id == params['tool_id']);
        if (tool) {
          await this.selectTool(tool);
        }
      }
    });
  }

  async getToolList() {
    try {
      this.loading = true;
      this.toolRunner = await this.client.ai.createToolRunner('');
      this.tools = await this.toolRunner.listAvailableTools();
      this.tools.sort((a, b) => new Date(b.updatedate).getTime() - new Date(a.updatedate).getTime());
      this.filterTools();
      this.cdRef.detectChanges();
    } catch (error) {
      console.error('Error fetching tools:', error);
    } finally {
      this.loading = false;
    }
  }

  async getVendors() {
    const vendors = await this.client.llm.listVendors();
    this.vendors = vendors;
  }

  async getModels(vendor: LlmVendor) {
    if (this.models[vendor]) {  
      return this.models[vendor];
    } else {
      const models = await this.client.llm.listModels({ vendor });
      this.models[vendor] = models;
      return models;
    }
  }

  async onVendorChange(vendor: LlmVendor) {
    if (vendor && this.selectedTool?.extraObj) {
      await this.getModels(vendor);
      // Reset the model when vendor changes
      this.selectedTool.extraObj['llmModel'] = '';
      this.cdRef.detectChanges();
    }
  }

  async updateTool() {
    try {
      console.log('updateTool', this.selectedTool);
      this.toolRunner.instantiate(this.selectedTool);
      console.log('toolRunner', this.toolRunner);
      await this.toolRunner.save();
      
      this.snackBar.open('Tool updated successfully', 'Close', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });
    } catch (error) {
      console.error('Error updating tool:', error);
      this.snackBar.open('Error updating tool: ' + error.message, 'Close', {
        duration: 5000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });
    }
  }

  filterTools() {
    if (!this.searchQuery) {
      this.filteredTools = [...this.tools];
      return;
    }

    const query = this.searchQuery.toLowerCase();
    this.filteredTools = this.tools.filter(tool => 
      tool.name?.toLowerCase().includes(query) || 
      tool.description?.toLowerCase().includes(query)
    );
  }

  async selectTool(tool: AiTool) {
    this.selectedTool = tool;
    this.paramValues = {};
    this.toolResult = null;
    this.isEditMode = false;

    this.toolRunner = await this.client.ai.createToolRunner(tool.name);
    await this.toolRunner.load();
    
    // Set required params string for the edit form
    this.requiredParamsStr = this.selectedTool.required?.join(',') || '';

    // Load models if tool has LLM parameters
    if (tool.extraObj && tool.extraObj['type'] === 'llm' && tool.extraObj['llmVendor']) {
      await this.getModels(tool.extraObj['llmVendor'] as LlmVendor);
    }

    // Add tool_id to the url using utilService
    // this.utilService.addUrlParamWithoutTriggeringNavigation('tool_id', tool._id?.toString());
    this.router.navigate([], {
      queryParams: { 'tool_id': tool._id?.toString() },
      queryParamsHandling: 'merge'
    });
    
    this.cdRef.detectChanges();
  }

  async runTool() {
    if (!this.selectedTool) {
      return;
    }
    
    try {
      this.isRunning = true;
      this.toolResult = null;
      
      // Convert parameter values (parse JSON for array types)
      const params: Record<string, any> = {};
      
      for (const key in this.paramValues) {
        if (this.paramValues[key]) {
          try {
            params[key] = JSON.parse(this.paramValues[key]);
          } catch (e) {
            // If it's not valid JSON, use as string
            params[key] = this.paramValues[key];
          }
        }
      }
      
      // Run the tool
      const result = await this.toolRunner.run(params);
      this.toolResult = result;
      
      this.snackBar.open('Tool executed successfully', 'Close', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });
    } catch (error) {
      console.error('Error running tool:', error);
      this.toolResult = { error: error.message || 'An error occurred' };
      
      this.snackBar.open('Error running tool', 'Close', {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });
    } finally {
      this.isRunning = false;
      this.cdRef.detectChanges();
    }
  }
  
  // Toggle between Run and Edit view
  toggleEditMode() {
    this.isEditMode = !this.isEditMode;
    if (this.isEditMode && this.selectedTool) {
      this.requiredParamsStr = this.selectedTool.required?.join(',') || '';
      this.initParameterJsonValues();
      this.initFactoryParameterJsonValues();
      this.selectedTool.extraObj = this.toolRunner['extraObj'];
    }
  }
  
  // Initialize parameter JSON values from selectedTool.kvParameters
  initParameterJsonValues() {
    this.paramJsonValues = [];
    if (this.selectedTool?.kvParameters) {
      this.selectedTool.kvParameters.forEach(param => {
        this.paramJsonValues.push({
          key: param.key,
          valueJson: JSON.stringify(param.value, null, 2)
        });
      });
    }
  }

  // Initialize factory parameter JSON values from selectedTool.factoryParameters
  initFactoryParameterJsonValues() {
    this.factoryParamJsonValues = [];
    if ((this.selectedTool as any)?.factoryParameters) {
      (this.selectedTool as any).factoryParameters.forEach((param: any) => {
        this.factoryParamJsonValues.push({
          key: param.key,
          valueJson: JSON.stringify(param.value, null, 2)
        });
      });
    }
  }
  
  // Add a new parameter
  addParam() {
    this.paramJsonValues.push({
      key: '',
      valueJson: JSON.stringify({
        type: 'string',
        description: ''
      }, null, 2)
    });
  }
  
  // Remove a parameter
  removeParam(index: number) {
    this.paramJsonValues.splice(index, 1);
  }

  // Add a new factory parameter
  addFactoryParam() {
    this.factoryParamJsonValues.push({
      key: '',
      valueJson: JSON.stringify({
        type: 'string',
        description: ''
      }, null, 2)
    });
  }
  
  // Remove a factory parameter
  removeFactoryParam(index: number) {
    this.factoryParamJsonValues.splice(index, 1);
  }
  
  // Save changes to the tool
  async saveToolChanges() {
    if (!this.selectedTool) {
      return;
    }
    try {
      // Validate parameter JSON values
      let hasErrors = false;
      this.paramJsonValues.forEach(param => {
        try {
          param.error = undefined;
          JSON.parse(param.valueJson);
        } catch (e) {
          param.error = e.message;
          hasErrors = true;
        }
      });
      this.factoryParamJsonValues.forEach(param => {
        try {
          param.error = undefined;
          JSON.parse(param.valueJson);
        } catch (e) {
          param.error = e.message;
          hasErrors = true;
        }
      });
      if (hasErrors) {
        this.snackBar.open('Please fix parameter JSON errors before saving', 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
        });
        return;
      }
      // Process required parameters string back to array
      if (this.requiredParamsStr) {
        this.selectedTool.required = this.requiredParamsStr
          .split(',')
          .map(param => param.trim())
          .filter(param => param);
      } else {
        this.selectedTool.required = [];
      }
      // Update parameters from JSON values
      this.selectedTool.kvParameters = this.paramJsonValues
        .filter(param => param.key && param.key.trim() !== '')
        .map(param => ({
          key: param.key.trim(),
          value: JSON.parse(param.valueJson)
        }));
      // Update factoryParameters from JSON values
      (this.selectedTool as any).factoryParameters = this.factoryParamJsonValues
        .filter(param => param.key && param.key.trim() !== '')
        .map(param => ({
          key: param.key.trim(),
          value: JSON.parse(param.valueJson)
        }));
      // Ensure asAgentAsync is present
      if (typeof (this.selectedTool as any).asAgentAsync !== 'boolean') {
        (this.selectedTool as any).asAgentAsync = false;
      }
      // Call updateTool to save changes
      await this.updateTool();
      // await this.getToolList();
      this.toggleEditMode();
    } catch (error) {
      console.error('Error saving tool changes:', error);
      this.snackBar.open('Error saving tool changes: ' + error.message, 'Close', {
        duration: 5000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });
    }
  }
  
  // Helper method to determine if parameter is required
  isRequired(paramKey: string): boolean {
    return this.selectedTool?.required?.includes(paramKey) || false;
  }
  
  // Format JSON for display
  formatOutput(obj: any): any {
    if (typeof obj === 'string') {
      try {
        const json = JSON.parse(obj);
        return this.markdown.parse(`\`\`\`json\n${JSON.stringify(json, null, 2)}`);
      } catch (e) {
        return this.markdown.parse(`${obj}`);
      }
    } else {
      return this.markdown.parse(`\`\`\`json\n${JSON.stringify(obj, null, 2)}`);
    }
  }

  openSystemPromptModal() {
    const markdown = this.selectedTool?.extraObj?.['systemPrompt'] || '';
    this.dialog.open(MarkdownModalComponent, {
      data: { markdown } as MarkdownModalData,
      width: '700px',
      maxWidth: '95vw',
    });
  }
}